<script setup lang="ts">
import { computed, ref } from 'vue';
import router from '@/router';
import type { ProjectWithSites } from '../models/Project';
import AddIcon from '@shared/assets/icon/add.svg';
import ChevronRightSVG from '@shared/assets/icon/chevron-right.svg';
import CheckCircleFilledIcon from '@shared/assets/icon/check-circle-filled.svg';
import ButtonSecondary from '@/shared/components/button/ButtonSecondary.vue';
import ButtonOutlined from '@/shared/components/button/ButtonOutlined.vue';
import WarningBox from './WarningBox.vue';
import { projectHeaderDisplay } from '../utils/project-and-site';
import { useDeviceSpecificationStore } from '../stores/deviceSpecificationStore';

interface Props {
  project: ProjectWithSites;
  canEdit?: boolean;
  canAddSite?: boolean;
  colorScheme?: 'white' | 'dark';
}

const props = withDefaults(defineProps<Props>(), {
  canEdit: true,
  canAddSite: true,
  colorScheme: 'white',
});

const errorOnSelectedSite = ref<number | undefined>(undefined);

const deviceSpecificationStore = useDeviceSpecificationStore();

const projectHeader = computed(() => {
  return projectHeaderDisplay(props.project.id, props.project.name);
});

const isSiteAvailableToCommission = (siteId: number) => {
  const site = props.project.sites.find((site) => site.id === siteId);
  return site && site.commissionedSolshareCount < site.plannedSolshareCount;
};

const selectSiteToConfigure = (siteId: number) => {
  if (!isSiteAvailableToCommission(siteId)) {
    errorOnSelectedSite.value = siteId;
    return;
  }

  const site = props.project.sites.find((site) => site.id === siteId);
  deviceSpecificationStore.updateSelectedSite({
    projectId: props.project.id,
    projectName: props.project.name,
    country: props.project.country,
    siteId: siteId,
    siteAddress: site?.address ?? '',
    sitePostcode: site?.postcode ?? null,
  });
  router.push({ name: 'ConfigureSolshareConfirmSite' });
};

const addAnotherSiteToProject = () => {
  router.push({ name: 'ProjectAddSite', params: { projectId: props.project.id } });
};

const goToEditProject = () => {
  router.push({ name: 'ProjectEdit', params: { projectId: props.project.id } });
};

const allSitesAreCommissioned = computed(() => {
  return props.project.sites.every(
    (site) => site.commissionedSolshareCount >= site.plannedSolshareCount,
  );
});
</script>

<template>
  <div :class="['container', 'box-shadow', `color-scheme-${colorScheme}`]">
    <div class="project-header">
      <h2 class="project-header__title">
        {{ projectHeader.heading }}
        <span v-if="projectHeader.subheading" class="project-header__subtitle">
          {{ projectHeader.subheading }}
        </span>
      </h2>
    </div>

    <div class="project-content">
      <div class="secondary-text site-header">
        {{ allSitesAreCommissioned ? 'List of sites:' : 'Select a site to commission:' }}
      </div>
      <template v-for="site in project.sites" :key="site.id">
        <template v-if="errorOnSelectedSite === site.id">
          <div class="site-error-container">
            <WarningBox type="alert">
              <template #title>All planned SolShares on this site are commissioned.</template>
              <template #message>Edit the project to add more SolShares.</template>
            </WarningBox>
          </div>
        </template>

        <button
          :class="[
            'site-container',
            'invisible-button',
            { 'alert-state': errorOnSelectedSite === site.id },
          ]"
          :aria-label="`Select site with address: ${site.address}`"
          :data-tn="`btn-select-site-${site.id}`"
          @click="selectSiteToConfigure(site.id)"
        >
          <div
            :class="[
              'site-container__cell',
              'first',
              'site-container__address',
              { 'site-container__address--selectable': isSiteAvailableToCommission(site.id) },
            ]"
          >
            {{ site.address }}
            <span v-if="isSiteAvailableToCommission(site.id)" class="site-address__chevron-icon">
              <ChevronRightSVG />
            </span>
          </div>
          <div class="site-container__cell last" data-tn="solshare-count-cell">
            <div class="solshare-count__container">
              <span class="solshare-count__label secondary-text"> SolShare(s) commissioned: </span>

              <span class="solshare-count__count">
                {{ site.commissionedSolshareCount }} /
                {{
                  site.plannedSolshareCount >= site.commissionedSolshareCount
                    ? site.plannedSolshareCount
                    : site.commissionedSolshareCount
                }}
              </span>

              <span class="solshare-count__fully-commissioned-icon">
                <CheckCircleFilledIcon v-if="!isSiteAvailableToCommission(site.id)" />
              </span>
            </div>
          </div>
        </button>
      </template>
    </div>

    <div class="project-footer">
      <ButtonSecondary
        v-if="canAddSite"
        class="add-site-button"
        block
        @click="addAnotherSiteToProject"
        data-tn="add-site-to-project-btn"
      >
        <template #iconLeft>
          <AddIcon />
        </template>
        Add another site to this project
      </ButtonSecondary>

      <ButtonOutlined
        v-if="canEdit"
        block
        size="small"
        @click="goToEditProject"
        data-tn="edit-project-btn"
      >
        Edit project
      </ButtonOutlined>
    </div>
  </div>
</template>

<style lang="less" scoped>
.color-scheme-white {
  background-color: white;

  .site-container__cell {
    border-color: @color-primary;
  }
}

.color-scheme-dark {
  background-color: @color-background-dark;

  .site-container__cell {
    border-color: @color-mid-tone;
  }

  .site-container__address--selectable {
    background-color: @color-primary;
    color: white;
  }
}

.container {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  padding: @content-side-margin;
  margin-bottom: @content-side-margin;

  border: 1px solid @color-mid-tone;
  border-radius: @border-radius-large;
}

.project-header {
  margin-bottom: 14px;

  &__title {
    text-align: center;
    font-weight: @font-weight-regular !important;
  }

  &__subtitle {
    padding-top: 4px;
    display: block;
    font-size: 1rem;
    font-weight: @font-weight-bold !important;
  }
}

.secondary-text {
  color: @color-secondary;
  font-weight: @font-weight-medium;
}

.site-header {
  margin-bottom: 8px;
}

.site-error-container {
  margin-bottom: 8px;
}

.site-container {
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-bottom: 14px;
  color: @color-primary;
  font-weight: @font-weight-medium;

  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  border-radius: @border-radius-medium; // Need for box-shadow to works on corners

  &.invisible-button {
    /* Reset default button styles */
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
    outline: inherit;
    text-align: left;
  }

  &__cell {
    background-color: white;

    padding: 14px 18px;
    border-width: 1px;
    border-style: solid;
    width: 100%;

    &.first {
      border-top-left-radius: @border-radius-medium;
      border-top-right-radius: @border-radius-medium;
      border-bottom: none;
    }

    &.last {
      border-bottom-left-radius: @border-radius-medium;
      border-bottom-right-radius: @border-radius-medium;
    }
  }

  &.alert-state {
    .site-container__cell {
      border-color: @color-alert;
    }
  }
}

.site-container__address {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 0.75rem;

  color: @color-primary;
  font-weight: @font-weight-medium;

  &--selectable {
    background-color: @color-mid-tone;
  }

  .site-address__chevron-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 2.2rem;
      height: 2.2rem;
    }
  }
}

.solshare-count {
  &__container {
    display: flex;
    align-items: center;
  }

  &__label {
    margin-right: 14px;
    flex-basis: 60%;
    align-self: flex-start;
  }

  &__count {
    flex-basis: 30%;
    align-self: flex-end;
    text-align: right;
  }

  &__fully-commissioned-icon {
    flex-basis: 10%;
    align-self: flex-end;

    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-left: 6px;
    color: @color-primary;
    vertical-align: middle;

    svg {
      width: 20px;
      height: 18px;
    }
  }
}

.project-footer {
  margin-top: 8px;

  .add-site-button {
    margin-bottom: 8px;
  }
}
</style>
